import React, { useState, useEffect } from 'react';
import { X, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ITALIAN_PROVINCES } from '../constants/cities';

interface NewClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (clientData: any) => Promise<void>;
  initialData?: any;
}

const NewClientModal: React.FC<NewClientModalProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const { user } = useAuth();
  const [clientData, setClientData] = useState({
    name: '',
    email: '',
    phone: '',
    birthdate: '',
    gender: '',
    city: 'ROMA',
    contact_status: 'attivo',
    status_notes: ''
  });
  const [creator, setCreator] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      if (initialData) {
        setClientData({
          name: initialData.name || '',
          email: initialData.email || '',
          phone: initialData.phone || '',
          birthdate: initialData.birthdate || '',
          gender: initialData.gender || '',
          city: initialData.city || 'ROMA',
          contact_status: initialData.contact_status || 'attivo',
          status_notes: initialData.status_notes || ''
        });

        if (initialData.created_by) {
          loadCreatorInfo(initialData.created_by);
        }
      } else {
        setClientData({
          name: '',
          email: '',
          phone: '',
          birthdate: '',
          gender: '',
          city: 'ROMA',
          contact_status: 'attivo',
          status_notes: ''
        });
        setCreator(null);
      }
    }
  }, [isOpen, initialData]);

  const loadCreatorInfo = async (creatorId: string) => {
    try {
      setLoading(true);
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('name, role')
        .eq('id', creatorId)
        .single();

      if (error) throw error;
      setCreator(profile);
    } catch (error) {
      console.error('Error loading creator info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const formattedData = {
      ...clientData,
      name: clientData.name.toUpperCase(),
      created_by: initialData?.created_by || user?.id
    };
    try {
      await onSubmit(formattedData);
      onClose();
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('contacts_phone_unique')) {
        setError('Questo numero di telefono è già associato a un altro contatto.');
      } else if (msg.includes('contacts_email_unique')) {
        setError('Questa email è già associata a un altro contatto.');
      } else {
        setError(msg || 'Errore nel salvare il contatto.');
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientData({
      ...clientData,
      name: e.target.value.toUpperCase()
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Modifica Cliente' : 'Nuovo Cliente'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome Completo</label>
              <input
                type="text"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500 uppercase"
                value={clientData.name}
                onChange={handleNameChange}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={clientData.email}
                onChange={(e) => setClientData({ ...clientData, email: e.target.value })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Telefono</label>
              <input
                type="tel"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={clientData.phone}
                onChange={(e) => setClientData({ ...clientData, phone: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Sesso</label>
              <select
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={clientData.gender}
                onChange={(e) => setClientData({ ...clientData, gender: e.target.value })}
              >
                <option value="">Seleziona sesso</option>
                <option value="uomo_etero">Uomo Etero</option>
                <option value="donna_etero">Donna Etero</option>
                <option value="uomo_gay">Uomo Gay</option>
                <option value="donna_gay">Donna Gay</option>
                <option value="non_dichiarato">Non dichiarato</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Data di Nascita</label>
              <input
                type="date"
                required
                max={new Date().toISOString().split('T')[0]}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={clientData.birthdate}
                onChange={(e) => setClientData({ ...clientData, birthdate: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Città</label>
              <select
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={clientData.city}
                onChange={(e) => setClientData({ ...clientData, city: e.target.value })}
              >
                {ITALIAN_PROVINCES.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Stato Contatto</label>
              <select
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                value={clientData.contact_status}
                onChange={(e) => setClientData({ ...clientData, contact_status: e.target.value })}
              >
                <option value="attivo">Attivo</option>
                <option value="da_ricontattare">Da controllare</option>
                <option value="in_una_relazione">In una relazione</option>
                <option value="non_interessato">Non interessato</option>
                <option value="blacklist">Blacklist</option>
                <option value="vip">VIP</option>
              </select>
            </div>

            {(clientData.contact_status === 'non_interessato' || clientData.contact_status === 'blacklist') && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Note Status</label>
                <textarea
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
                  value={clientData.status_notes}
                  onChange={(e) => setClientData({ ...clientData, status_notes: e.target.value })}
                  placeholder="Motivo per cui non è più interessato o è in blacklist..."
                />
              </div>
            )}

            {/* Creator information */}
            {initialData && (
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-gray-500">Scheda creata da</p>
                <div className="flex items-center gap-2 mt-1">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="font-medium">{creator?.name || 'Utente non trovato'}</span>
                  <span className="text-sm text-gray-500 capitalize">({creator?.role || 'ruolo sconosciuto'})</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700"
            >
              {initialData ? 'Salva Modifiche' : 'Aggiungi Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewClientModal;